import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, input, model, output, signal } from '@angular/core';
import { SvgComponent } from '@shared/components/svg';
import { ClickOutsideDirective } from '@shared/directives';
import { LabelKey, Option } from '@shared/models';

@Component({
  selector: 'app-select',
  imports: [ClickOutsideDirective, NgClass, SvgComponent],
  templateUrl: './select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent {
  readonly options = model.required<LabelKey[]>();
  readonly dropdownPosition = input<'LEFT' | 'CENTER' | 'RIGHT'>('LEFT');
  readonly displayAsBlock = input(false);
  readonly placeholder = input<string>('');
  readonly disabled = input(false);
  readonly optionSelected = output<string>();
  readonly hasMultipleOptions = computed(() => this.options().length > 1);
  readonly selected = signal<LabelKey | null>(null);
  protected readonly isOpen = signal<boolean>(false);

  constructor() {
    effect(() => {
      const options = this.options();
      if (options && options.length === 1) {
        const selected = options[0]!;
        this.selected.set(selected);
        this.optionSelected.emit(selected.key);
        this.close();
      } else {
        this.selected.set(null);
        this.optionSelected.emit('');
      }
    });
  }

  /**
   * Toggles the visibility of the dropdown.
   * This implementation is correct and efficient.
   */
  toggleSelect(): void {
    if (this.hasMultipleOptions()) this.isOpen.update((current) => !current);
  }

  /**
   * Handles the selection of an option.
   * @param option The option that was selected.
   */
  select(option: Option): void {
    this.selected.set(option);
    this.optionSelected.emit(option.key);
    this.close(); // Reuse the close method for consistency.
  }

  /**
   * Handles keydown events for managing component state and user interactions.
   *
   * Depending on the pressed key, this method can:
   * - Close the component if it's open (`Tab` or `Escape` key).
   * - Toggle selection if `Enter` or `Space` is pressed, provided multiple options are available.
   *
   * @param event The keyboard event triggering the action.
   * @return void This method does not return a value but may alter the component's state or prevent default behavior.
   * @remarks Prevents default behavior for `Enter` and `Space` keys to avoid unintended actions.
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab' && this.isOpen()) this.close();
    if (event.key === 'Escape' && this.isOpen()) this.close();
    if ((event.key === 'Enter' || event.key === ' ') && this.hasMultipleOptions()) {
      this.toggleSelect();
      event.preventDefault();
    }
  }

  /**
   * Closes the select dropdown.
   * This method is public to be accessible from the component's template,
   * for example with the (clickOutside) directive.
   */
  public close(): void {
    this.isOpen.set(false);
  }
}
